/**
 * Allowlist sanitizer for Curate `rich_text` block HTML.
 *
 * WHY THIS EXISTS
 *
 * Curate content is imported from Kai-Studio export bundles. A content
 * administrator POSTs a bundle, the API stores each `rich_text` block's `html`
 * payload verbatim, and every provider assigned that article renders it. That
 * is a stored-XSS path: the HTML crosses a trust boundary (uploaded by one
 * user, executed in another user's authenticated session) and reaches
 * `dangerouslySetInnerHTML`. So everything here is allowlist, never blocklist.
 *
 * WHY INLINE STYLES ARE PRESERVED (FILTERED, NOT STRIPPED)
 *
 * Kai-Studio articles are authored as self-contained styled HTML: step cards,
 * numbered chips, and pills are built with inline flexbox, and — the important
 * part — their declarations reference the host app's design tokens
 * (`var(--color-text-muted)`, `var(--radius-lg)`, ...). The content is designed
 * to inherit this app's theme, including dark mode, through those variables.
 * Stripping `style` therefore doesn't just lose decoration, it collapses the
 * authored layout entirely.
 *
 * So instead of dropping `style`, each declaration is filtered against a
 * property allowlist plus a value check that rejects the constructs that make
 * inline CSS dangerous: `url(...)` (network callbacks / exfiltration),
 * `expression(...)` (legacy code execution), and `position: fixed | sticky`
 * (escape the reader to overlay app chrome). Layout, spacing, color, and
 * typography pass through untouched.
 *
 * WHY SVG IS ALLOWED (NARROWLY)
 *
 * The content embeds small decorative icons as inline `<svg><path/></svg>`.
 * SVG's dangerous constructs are specific and enumerable — `<script>`,
 * `<foreignObject>`, `<use href>`, animation elements, event handlers — so SVG
 * subtrees are walked with their own strict element/attribute allowlist
 * (shapes and paint only) rather than dropped. Anything unrecognized inside an
 * SVG is removed with its subtree, never unwrapped, because SVG internals are
 * meaningless as prose.
 *
 * MECHANISM
 *
 * `DOMParser` parses into an inert document: no scripts execute, no resources
 * load, nothing attaches to the live DOM. The walk then removes anything not
 * explicitly allowed. Regex-based sanitizers are a known source of bypasses;
 * the browser's own parser resolves the markup exactly as it will render.
 *
 * DOMPurify remains the better long-term answer; this module is deliberately a
 * drop-in shape for it (`sanitizeHtml(dirty) -> clean`). If it lands as a
 * dependency, replace the body here and every call site is already correct.
 */

/**
 * Elements permitted in article body copy. Formatting and structure only.
 * Notably absent and never allowed: script, iframe, object, embed, form,
 * input, button (execution / credential capture), link, meta, base
 * (document-level injection), and math (script-bearing in some parsers).
 * svg is NOT in this set — it is routed to the dedicated SVG walk below.
 */
const ALLOWED_TAGS = new Set([
  'p', 'br', 'hr', 'span', 'div',
  'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'del', 'ins', 'mark', 'small', 'sub', 'sup',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'blockquote', 'q', 'cite',
  'code', 'pre', 'kbd', 'samp', 'var',
  'a',
  'img',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
  'figure', 'figcaption',
  'abbr', 'time', 'dfn',
  'dl', 'dt', 'dd',
]);

const GLOBAL_ATTRS = new Set(['title', 'dir', 'lang', 'style']);

/** Per-tag additional attributes. */
const TAG_ATTRS: Record<string, Set<string>> = {
  a: new Set(['href', 'target', 'rel']),
  img: new Set(['src', 'alt', 'width', 'height', 'loading']),
  td: new Set(['colspan', 'rowspan', 'headers']),
  th: new Set(['colspan', 'rowspan', 'scope', 'headers']),
  col: new Set(['span']),
  colgroup: new Set(['span']),
  time: new Set(['datetime']),
  abbr: new Set(['title']),
  ol: new Set(['start', 'type', 'reversed']),
};

/* Inline style filtering
 * -------------------------------------------------------------------------- */

/**
 * CSS properties Kai-Studio content may set. Derived from what the export
 * bundles actually use (layout, box, typography, paint) plus their obvious
 * siblings, so an author using `margin-right` doesn't get stripped where
 * `margin-left` passed. Deliberately absent: `z-index` (stacking escape),
 * `pointer-events` (click interception), `cursor` (UI spoofing), `content`,
 * `filter`/`backdrop-filter`, and every animation/transition property.
 */
const ALLOWED_STYLE_PROPS = new Set([
  // paint
  'color', 'background', 'background-color', 'opacity', 'box-shadow',
  // border
  'border', 'border-top', 'border-right', 'border-bottom', 'border-left',
  'border-radius', 'border-color', 'border-style', 'border-width',
  'outline', 'outline-offset',
  // box
  'padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
  'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
  'width', 'height', 'min-width', 'min-height', 'max-width', 'max-height',
  'overflow', 'overflow-x', 'overflow-y', 'aspect-ratio', 'object-fit',
  // typography
  'font-size', 'font-weight', 'font-style', 'line-height', 'letter-spacing',
  'text-align', 'text-transform', 'text-decoration', 'text-overflow',
  'white-space', 'word-break', 'vertical-align', 'list-style',
  // layout
  'display', 'position', 'top', 'right', 'bottom', 'left',
  'flex', 'flex-direction', 'flex-wrap', 'flex-grow', 'flex-shrink', 'flex-basis',
  'gap', 'row-gap', 'column-gap', 'align-items', 'align-self', 'align-content',
  'justify-content', 'justify-self', 'order',
  'grid-template-columns', 'grid-template-rows', 'grid-column', 'grid-row',
]);

/**
 * Value-level rejections, applied to every declaration:
 *
 *   url( / image-set( / element( / paint(  — network callbacks and exfiltration
 *   expression( / behavior / -moz-binding  — legacy code execution
 *   javascript                             — scheme smuggling inside a value
 *   @ / < / \                              — at-rules, markup, escape smuggling
 *
 * `var(...)` is allowed and is the common case: Kai-Studio content references
 * this app's design tokens, which is how it inherits theming and dark mode.
 */
const FORBIDDEN_VALUE = /(url\s*\(|image-set\s*\(|element\s*\(|paint\s*\(|expression\s*\(|behavior|-moz-binding|javascript|@|<|\\)/i;

/** `position: fixed | sticky` lets content escape the reader and overlay app chrome. */
const FORBIDDEN_POSITION = /^(fixed|sticky)$/i;

/**
 * Filter one style attribute down to its allowed declarations. Returns the
 * surviving declarations, or an empty string when nothing survives (in which
 * case the attribute is removed entirely).
 */
export function sanitizeStyle(style: string): string {
  const kept: string[] = [];
  for (const raw of style.split(';')) {
    const decl = raw.trim();
    if (!decl) continue;
    const colon = decl.indexOf(':');
    if (colon === -1) continue;
    const prop = decl.slice(0, colon).trim().toLowerCase();
    const value = decl.slice(colon + 1).trim();
    if (!ALLOWED_STYLE_PROPS.has(prop)) continue;
    if (FORBIDDEN_VALUE.test(value)) continue;
    if (prop === 'position' && FORBIDDEN_POSITION.test(value)) continue;
    kept.push(`${prop}:${value}`);
  }
  return kept.join(';');
}

/* URL filtering
 * -------------------------------------------------------------------------- */

/**
 * URL schemes allowed on `href`/`src`. Anything not matching is dropped, which
 * covers `javascript:`, `data:` (SVG/HTML payloads), and `vbscript:`.
 * Protocol-relative (`//host`), same-document (`#anchor`), and relative paths
 * are allowed.
 */
const SAFE_URL = /^(?:https?:|mailto:|tel:|[#/](?![/\\])|\.{1,2}\/)/i;

const isSafeUrl = (value: string): boolean => {
  // Strip C0/C1 control characters and whitespace before testing. The URL
  // parser ignores those, so `java\tscript:`, `java\nscript:` and
  // `java script:` all resolve back to `javascript:` — testing the raw
  // string would let every one of them through.
  // Matching control characters is the entire point here: they are the
  // bypass vector being removed, not an accidental inclusion.
  // eslint-disable-next-line no-control-regex
  const normalized = value.replace(/[\u0000-\u0020\u007F-\u00A0]/g, '').toLowerCase();
  if (normalized.startsWith('//')) return true;
  return SAFE_URL.test(normalized);
};

/* SVG
 * -------------------------------------------------------------------------- */

/**
 * Shapes and grouping only. Deliberately absent: script, foreignObject
 * (arbitrary HTML), use (href indirection), image, animate/set/animateMotion
 * (attribute injection over time), filter, and a.
 */
const SVG_ALLOWED_TAGS = new Set([
  'svg', 'g', 'path', 'circle', 'ellipse', 'rect', 'line', 'polyline', 'polygon', 'defs', 'title',
]);

/** Geometry and paint. No href/xlink:href anywhere, no event handlers. */
const SVG_ALLOWED_ATTRS = new Set([
  'viewbox', 'xmlns', 'width', 'height', 'preserveaspectratio',
  'fill', 'fill-rule', 'fill-opacity', 'clip-rule',
  'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'stroke-dasharray',
  'stroke-dashoffset', 'stroke-opacity', 'stroke-miterlimit',
  'd', 'cx', 'cy', 'r', 'rx', 'ry', 'x', 'y', 'x1', 'y1', 'x2', 'y2', 'points',
  'opacity', 'transform', 'style',
  'aria-hidden', 'focusable', 'role',
]);

/**
 * Walk an `<svg>` subtree with the SVG allowlist. Unlike the HTML walk,
 * disallowed elements are removed with their whole subtree — unwrapping SVG
 * internals into surrounding markup produces garbage, not prose.
 */
function sanitizeSvg(svg: Element): void {
  const walk = (node: Element): void => {
    for (const child of Array.from(node.children)) {
      if (!SVG_ALLOWED_TAGS.has(child.tagName.toLowerCase())) {
        child.remove();
        continue;
      }
      scrubSvgAttributes(child);
      walk(child);
    }
  };
  scrubSvgAttributes(svg);
  walk(svg);
}

function scrubSvgAttributes(el: Element): void {
  for (const attr of Array.from(el.attributes)) {
    const name = attr.name.toLowerCase();
    if (!SVG_ALLOWED_ATTRS.has(name) || name.startsWith('on') || name.includes('href')) {
      el.removeAttribute(attr.name);
      continue;
    }
    if (name === 'style') {
      const clean = sanitizeStyle(attr.value);
      if (clean) el.setAttribute('style', clean);
      else el.removeAttribute(attr.name);
    }
  }
}

/* HTML
 * -------------------------------------------------------------------------- */

/**
 * Sanitize untrusted HTML to an allowlisted subset, preserving the safe part
 * of Kai-Studio's inline styling.
 *
 * Returns a string safe to hand to `dangerouslySetInnerHTML`. Disallowed
 * elements are unwrapped rather than deleted where they carry text (so a
 * `<font>` wrapper loses the tag but keeps its words), except for elements
 * whose content is itself dangerous or meaningless as prose, which are removed
 * outright along with their subtree.
 *
 * Falls back to returning an empty string in non-DOM environments (SSR, some
 * test runners) rather than risking passing raw markup through.
 */
export function sanitizeHtml(dirty: string | null | undefined): string {
  if (!dirty) return '';
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return '';

  let doc: Document;
  try {
    doc = new window.DOMParser().parseFromString(`<body>${dirty}</body>`, 'text/html');
  } catch {
    return '';
  }
  if (!doc?.body) return '';

  // Subtrees that must vanish entirely — keeping their text would surface
  // script source or stylesheet rules as visible prose.
  const DROP_SUBTREE = new Set([
    'script', 'style', 'iframe', 'object', 'embed', 'template', 'noscript', 'math', 'form',
  ]);

  const walk = (node: Element): void => {
    // Snapshot children: the list mutates as we unwrap and remove.
    const children = Array.from(node.children);
    for (const child of children) {
      const tag = child.tagName.toLowerCase();

      if (DROP_SUBTREE.has(tag)) {
        child.remove();
        continue;
      }

      // SVG gets its own strict walk and no recursion into the HTML rules.
      if (tag === 'svg') {
        sanitizeSvg(child);
        continue;
      }

      if (!ALLOWED_TAGS.has(tag)) {
        // Unwrap: promote the element's children in place, then drop the tag.
        // Preserves the words an author wrote inside an unsupported wrapper.
        const parent = child.parentNode;
        if (parent) {
          while (child.firstChild) parent.insertBefore(child.firstChild, child);
          parent.removeChild(child);
        }
        continue;
      }

      // Strip attributes before recursing so a malicious handler cannot fire
      // during subsequent processing.
      for (const attr of Array.from(child.attributes)) {
        const name = attr.name.toLowerCase();
        const allowed = GLOBAL_ATTRS.has(name) || TAG_ATTRS[tag]?.has(name);

        // Every `on*` handler, plus anything not explicitly allowed.
        if (!allowed || name.startsWith('on')) {
          child.removeAttribute(attr.name);
          continue;
        }
        if ((name === 'href' || name === 'src') && !isSafeUrl(attr.value)) {
          child.removeAttribute(attr.name);
          continue;
        }
        if (name === 'style') {
          const clean = sanitizeStyle(attr.value);
          if (clean) child.setAttribute('style', clean);
          else child.removeAttribute(attr.name);
        }
      }

      // Any surviving link opens in a new tab without handing the opener a
      // window reference (reverse tabnabbing).
      if (tag === 'a' && child.hasAttribute('href')) {
        child.setAttribute('target', '_blank');
        child.setAttribute('rel', 'noopener noreferrer nofollow');
      }

      walk(child);
    }
  };

  walk(doc.body);
  return doc.body.innerHTML;
}
